import type { ProfilePage } from "@amber/application/contracts";
import { FIELD_STATUS_LABELS, IDENTITY_FIELD_LABELS } from "@amber/profiles/contracts";
import { NamesAndRoles } from "@amber/profiles/ui";
import { Button, Tag, cx, formatDate } from "@amber/ui";

type Identity = ProfilePage["identity"];

function factTone(status: Identity["fields"][number]["status"]) {
  if (status === "conflict") return "waiting";
  if (status === "sourced") return "neutral";
  return "quiet";
}

function accountNote(a: Identity["accounts"][number]): string {
  if (a.ended) return `ended ${a.ended}`;
  return a.rank === "deprecated" ? "deprecated" : "";
}

function IdentityFacts({ id }: { id: Identity }) {
  return (
    <>
      <h2 className="m-0 mt-5 text-xs font-semibold tracking-[0.06em] text-muted uppercase">Identity facts</h2>
      <ul className="m-0 mt-1.5 list-none p-0">
        {id.fields.map((f) => (
          <li
            key={f.field}
            className="grid grid-cols-[150px_minmax(0,1fr)_auto] items-start gap-3 border-t border-divider py-2"
          >
            <span className="text-muted">{IDENTITY_FIELD_LABELS[f.field]}</span>
            <span>
              <span className={cx("block", !f.value && "text-muted")}>
                {f.value ?? (f.status === "conflict" ? f.values.map((v) => v.value).join(" or ") : "Unknown")}
              </span>
              <span className="block text-xs text-muted">{f.evidence}</span>
            </span>
            <Tag tone={factTone(f.status)}>{FIELD_STATUS_LABELS[f.status]}</Tag>
          </li>
        ))}
      </ul>
      {id.open_questions.length > 0 ? (
        <>
          <h2 className="m-0 mt-5 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
            Missing or conflicting
          </h2>
          <ul className="m-0 mt-1 list-disc pl-4 text-md text-muted">
            {id.open_questions.map((q) => (
              <li key={`${q.field}-${q.detail}`}>{q.detail}</li>
            ))}
          </ul>
        </>
      ) : null}
      {id.accounts.length > 0 ? (
        <>
          <h2 className="m-0 mt-5 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
            Accounts listed on Wikidata
          </h2>
          <ul className="m-0 mt-1.5 list-none p-0">
            {id.accounts.map((a) => (
              <li
                key={`${a.platform}-${a.handle}`}
                className="grid grid-cols-[150px_minmax(0,1fr)_auto] gap-3 border-t border-divider py-2"
              >
                <span className="text-muted">{a.platform}</span>
                <a href={a.url} target="_blank" rel="noreferrer" className="truncate">
                  {a.url}
                </a>
                <span className="text-md text-muted">{accountNote(a)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

export function IdentityTab({
  profile,
  onEdit,
  onRefresh,
  editReason,
}: {
  profile: ProfilePage;
  onEdit: () => void;
  onRefresh: () => void;
  editReason: string | null;
}) {
  const id = profile.identity;
  return (
    <div className="overflow-auto px-4 pb-6 text-ui">
      <div className="mt-1 mb-2 flex gap-2">
        <Button size="sm" disabledReason={editReason} onClick={onEdit}>
          Edit identity…
        </Button>
        <Button
          size="sm"
          disabledReason={editReason ?? (id.wikidata_id ? null : "No Wikipedia identity to refresh from")}
          onClick={onRefresh}
        >
          Refresh from Wikipedia…
        </Button>
      </div>
      <dl className="m-0 mt-1 grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5">
        <dt className="text-muted">Identity confidence</dt>
        <dd className="m-0">
          {id.identity_confidence === null ? "Not assessed" : `${id.identity_confidence.toFixed(2)} · model estimate`}
        </dd>
        <dt className="text-muted">Last verified</dt>
        <dd className="m-0">
          {id.last_verified_at ? (
            <>
              {formatDate(id.last_verified_at)} {id.stale ? <Tag tone="waiting">Stale</Tag> : null}
            </>
          ) : (
            "Never verified"
          )}
        </dd>
        <dt className="text-muted">Tracking status</dt>
        <dd className="m-0">{profile.state_label}</dd>
        <dt className="text-muted">Wikidata</dt>
        <dd className="m-0">
          {id.wikidata_id && id.wikidata_url ? (
            <a href={id.wikidata_url} target="_blank" rel="noreferrer">
              {id.wikidata_id}
            </a>
          ) : (
            "Not linked"
          )}
        </dd>
        <dt className="text-muted">Wikipedia</dt>
        <dd className="m-0">
          {id.editions.length === 0
            ? "No article linked"
            : id.editions.map((e) => (
                <span key={e.edition} className="mr-3">
                  {e.url && e.state !== "missing" ? (
                    <a href={e.url} target="_blank" rel="noreferrer">
                      {e.edition}.wikipedia
                    </a>
                  ) : (
                    `${e.edition}.wikipedia`
                  )}
                  {e.state !== "read" ? (
                    <span className="text-muted"> ({e.state === "missing" ? "no article" : "couldn't be read"})</span>
                  ) : null}
                </span>
              ))}
        </dd>
        <dt className="text-muted">Portrait</dt>
        <dd className="m-0">
          {id.picture ? (
            <>
              Wikimedia Commons ·{" "}
              <a href={id.picture.origin_url} target="_blank" rel="noreferrer">
                origin
              </a>{" "}
              · {id.picture.rights}
            </>
          ) : (
            "No portrait"
          )}
        </dd>
      </dl>
      <NamesAndRoles profile={profile} />
      <IdentityFacts id={id} />
    </div>
  );
}
